import ImageIcon from "@mui/icons-material/Image";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import LightGalleryComponent from "lightgallery/react";
import { HTMLAttributes, useRef } from "react";

import { FavouriteButton } from "../components";
import { computeImageSrcSetProperties, createImageSizes, vaultUrlLoader } from "../utils";
import { Wrapper, ImagesWrapper, GalleryImageWrapper } from "./ProductGalleryLayoutComponents";
import { LIGHT_GALLERY_LICENSE_KEY } from "./lightGalleryConfig";

export type ProductGalleryProps = {
    imageList: string[];
    showFavouriteButton?: boolean;
    isFavourite?: boolean;
    onFavouriteChange?: (isFavourite: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function ProductGallery({
    imageList,
    showFavouriteButton,
    isFavourite,
    onFavouriteChange,
    ...props
}: ProductGalleryProps) {
    const ref = useRef<ReturnType<typeof lightGallery> | undefined>(undefined);

    const openImage = (index: number) => {
        const instance = ref.current;
        if (instance) {
            instance.openGallery(index);
        }
    };
    return (
        <Wrapper>
            <ImagesWrapper {...props} data-single-image={imageList.length === 1}>
                {imageList.map((image, index) => {
                    return (
                        <GalleryImage
                            type={index === 0 ? "primary" : "secondary"}
                            key={index}
                            src={image}
                            onClick={() => {
                                openImage(index);
                            }}
                        />
                    );
                })}
            </ImagesWrapper>
            {showFavouriteButton && (
                <FavouriteButtonWrapper>
                    <FavouriteButton
                        size="medium"
                        isFavourite={isFavourite ?? false}
                        onChange={onFavouriteChange}
                        color="light"
                    />
                </FavouriteButtonWrapper>
            )}
            <LightGalleryComponent
                onInit={(detail) => {
                    ref.current = detail.instance;
                }}
                plugins={[lgThumbnail, lgZoom]}
                licenseKey={LIGHT_GALLERY_LICENSE_KEY}
                download={false}
                dynamic={true}
                loop={false}
                dynamicEl={imageList.map((x) => {
                    const data = computeImageSrcSetProperties(
                        [300, 600, 900, 1280, 1200, 1536, 2048, 2560, 3440, 3840, 4096],
                        {
                            srcFactory: ({ width }) => vaultUrlLoader({ src: x, width }),
                        }
                    );
                    return {
                        src: data.src,
                        srcset: data.srcSet,
                        thumb: vaultUrlLoader({ src: x, width: 200, height: 200 }),
                    };
                })}
            />
        </Wrapper>
    );
}

const Image = styled("img")({
    height: "100%",
    width: "100%",
    objectFit: "cover",
});

type GalleryImageProps = {
    src: string;
    type: "primary" | "secondary";
} & HTMLAttributes<HTMLDivElement>;

const SIZES: Record<GalleryImageProps["type"], string> = {
    primary: createImageSizes(700, "100vw"),
    secondary: createImageSizes(100, "100vw"),
};

const GalleryImageIconButton = styled(IconButton)(({ theme }) => ({
    right: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 10,
    position: "absolute",
    opacity: 0.5,
    transition: theme.transitions.create("opacity", { duration: theme.transitions.duration.short }),
}));

function GalleryImage({ src, type, ...props }: GalleryImageProps) {
    return (
        <GalleryImageWrapper {...props}>
            <Image
                {...computeImageSrcSetProperties([300, 600, 960, 1280, 1920, 2048, 2560, 3440, 3840, 4096], {
                    srcFactory: ({ width }) => {
                        return vaultUrlLoader({ src, width });
                    },
                })}
                alt={""}
                decoding="async"
                sizes={SIZES[type]}
                loading="lazy"
            />

            <GalleryImageIconButton className="gallery-image-icon-button">
                <ImageIcon htmlColor="#ffffff" />
            </GalleryImageIconButton>
        </GalleryImageWrapper>
    );
}

const FavouriteButtonWrapper = styled("div")(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
}));
